import { useEffect, useRef, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { clearAllStorage } from '../utils/authStorage';

const isTestEmail = (email: unknown): boolean =>
  typeof email === 'string' && email.startsWith('pozytron.test');

const isDeactivationError = (message: string): boolean =>
  message.includes('PreTokenGeneration failed') ||
  message.includes('dezaktywowane') ||
  message.includes('deactivated') ||
  message.includes('User is disabled');

const isExpiredError = (message: string): boolean =>
  message.includes('NotAuthorizedException') ||
  message.includes('Refresh Token has expired') ||
  message.includes('Token has been revoked') ||
  message.includes('Invalid Refresh Token') ||
  message.includes('User is not authorized');

/**
 * Hook to manage Cognito session lifecycle.
 *
 * On mount: performs an initial force-refresh to immediately detect
 * deactivated accounts. Returns sessionReady=true once the check completes
 * so the app can block rendering until session state is known.
 *
 * After mount: subscribes to Amplify Hub auth events. Amplify fires
 * tokenRefresh_failure whenever any token refresh fails — triggered by the
 * Axios 401 interceptor or Amplify's internal session management. This
 * replaces the previous 4-minute polling interval.
 *
 * Auto-logout is disabled for test accounts (pozytron.test+) so that a
 * globalSignOut triggered by one parallel E2E scenario does not force-logout
 * other concurrently running scenarios sharing the same Cognito user pool.
 */
export const useAutoTokenRefresh = () => {
  const isTestAccountRef = useRef(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const performLogout = async (reason?: string) => {
      if (isTestAccountRef.current) {
        return;
      }

      await signOut();
      await clearAllStorage();
      localStorage.setItem('logout-event', Date.now().toString());
      if (reason) {
        localStorage.setItem('logout-reason', reason);
      }
      window.location.href = '/login';
    };

    const handleRefreshError = async (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Token refresh failed';

      if (isDeactivationError(message)) {
        await performLogout('ACCOUNT_DEACTIVATED');
      } else if (isExpiredError(message)) {
        await performLogout();
      }
      // Network or transient errors — ignored, session continues
    };

    // Hub listener — Amplify fires tokenRefresh_failure whenever any
    // fetchAuthSession({ forceRefresh: true }) call fails (Axios 401
    // interceptor, Amplify internal refresh). Replaces the setInterval.
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event !== 'tokenRefresh_failure' || !mounted) {
        return;
      }
      void handleRefreshError(payload.data);
    });

    const init = async () => {
      // Cache email first so isTestAccountRef is set before any error path.
      try {
        const cached = await fetchAuthSession({ forceRefresh: false });
        const email = cached.tokens?.idToken?.payload?.['email'];
        if (mounted) {
          isTestAccountRef.current = isTestEmail(email);
        }
      } catch {
        // No session yet — treat as non-test account
      }

      if (!mounted) {
        return;
      }

      // One-time force-refresh on mount to immediately detect deactivated
      // accounts without waiting for the first API call to trigger a 401.
      try {
        await fetchAuthSession({ forceRefresh: true });
      } catch (error) {
        if (!mounted) {
          return;
        }
        const message =
          error instanceof Error ? error.message : 'Token refresh failed';

        if (isDeactivationError(message) || isExpiredError(message)) {
          await performLogout(
            isDeactivationError(message) ? 'ACCOUNT_DEACTIVATED' : undefined
          );
          return; // Page is navigating away — don't set sessionReady
        }
        // Transient error (network etc.) — fall through to setSessionReady
      }

      if (mounted) {
        setSessionReady(true);
      }
    };

    void init();

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return { sessionReady };
};
